import { useEffect, useRef, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { api, API, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

export const MediaPicker = ({ open, keyword, orientation, voice, onPick, onClose }) => {
  const [q, setQ] = useState(keyword || "");
  const [kind, setKind] = useState("video");
  const [source, setSource] = useState("pexels");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const reqId = useRef(0);

  const search = async (query = q, k = kind, src = source) => {
    const my = ++reqId.current;
    setLoading(true); setError(""); setItems([]);
    try {
      if (src === "gallery") {
        const { data } = await api.get("/gallery", { params: { kind: k } });
        if (my !== reqId.current) return;
        setItems(data.items.map((g) => ({ id: g.id, kind: g.kind, source: "gallery", gallery_id: g.id, thumb: `${API}/gallery/${g.id}/thumb`, duration: g.duration ? Math.round(g.duration) : null })));
      } else {
        if (!query) return setLoading(false);
        const { data } = await api.get("/media/search", { params: { q: query, orientation, kind: k, voice, source: src } });
        if (my === reqId.current) setItems(data);
      }
    }
    catch (e) { if (my === reqId.current) setError(errMsg(e)); } finally { if (my === reqId.current) setLoading(false); }
  };

  useEffect(() => { if (open) { setQ(keyword || ""); search(keyword, kind, source); } }, [open]); // eslint-disable-line

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-white/10 max-w-4xl" data-testid="media-picker-dialog">
        <DialogHeader><DialogTitle className="text-white">Elegir clip o imagen</DialogTitle></DialogHeader>
        <div className="flex gap-1 panel p-1 rounded-full w-fit" data-testid="media-source-tabs">
          {[["pexels", "Pexels"], ["pixabay", "Pixabay"], ["gallery", "Mi galería"]].map(([k, l]) => (
            <button key={k} data-testid={`media-source-${k}`} onClick={() => { setSource(k); search(q, kind, k); }}
              className={`px-4 py-1.5 rounded-full text-sm ${source === k ? "bg-[#E8C47C] text-black" : "text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); search(); }} className="flex gap-2">
          <Input data-testid="media-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar: ocean, city night, coffee…" className="bg-zinc-900/60" />
          {["video", "photo"].map((k) => (
            <Button key={k} type="button" variant="outline" data-testid={`media-kind-${k}`} onClick={() => { setKind(k); search(q, k, source); }}
              className={`border-white/10 ${kind === k ? "bg-[#E8C47C]/20 text-white" : ""}`}>{k === "video" ? "Videos" : "Fotos"}</Button>
          ))}
          <Button data-testid="media-search-btn" className="bg-[#E8C47C] hover:bg-[#F3D9A4] text-black font-semibold"><Search className="h-4 w-4" /></Button>
        </form>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="grid grid-cols-3 md:grid-cols-5 auto-rows-max content-start gap-3 max-h-[60vh] overflow-y-auto scroll-thin">
          {loading ? <div className="col-span-full py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#E8C47C]" /></div> :
            items.map((m) => (
              <button key={m.id} data-testid={`media-item-${m.id}`} onClick={() => { onPick(m); onClose(); }}
                className="group relative block w-full rounded-lg overflow-hidden border border-white/10 hover:border-[#E8C47C] aspect-[4/5]">
                <img src={m.thumb} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                {m.duration && <span className="absolute bottom-1 right-1 text-[10px] font-mono-tc bg-black/70 px-1.5 rounded">{m.duration}s</span>}
              </button>
            ))}
        </div>
        <p className="text-[11px] text-zinc-500">{source === "gallery" ? "Tus archivos subidos en Mi galería." : `Contenido gratuito proporcionado por ${source === "pixabay" ? "Pixabay" : "Pexels"}.`}</p>
      </DialogContent>
    </Dialog>
  );
};
